import React from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Task } from '@core/entities/task/Task'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { TaskAssignableUser } from '@core/entities/task/TaskAssignableUser'
import { useRequestChangesTask } from '@core/react-query/features/tasks/task'
import { useNavigationContext } from '@common/hooks'
import { localizedStrings } from '@core/strings'
import {
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@common/material'
import { Flex } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import ClearIcon from '@mui/icons-material/Clear'
import { TaskAssignUserPicker } from '@elements'
import { MAX_TASK_SUMMARY_CHARACTERS } from '@core/components/templateAnswersForm'

interface ComponentType {
  task: Task
}

type FormData = {
  assigneeUser: TaskAssignableUser
  comment: string
}

export const RequestChangesTaskModal: React.FC<ComponentType> = ({ task }) => {
  const { projectId } = useNavigationContext()
  const toast = useToast()
  const { hideModal } = useModal()
  const { control, handleSubmit } = useForm({
    defaultValues: {
      assigneeUser: TaskAssignableUser.new(task?.getPreviousStepSubmittedBy()),
      comment: '',
    },
  })

  const requestChangesTaskMutation = useRequestChangesTask()

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    requestChangesTaskMutation.mutate(
      {
        id: task.id,
        assigneeUserId: data.assigneeUser.id,
        comment: data.comment,
      },
      {
        onSuccess: () => {
          toast.success('Request changes successfully')
        },
        onError: (error) => {
          toast.error(error.message)
        },
        onSettled: () => {
          hideModal()
        },
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.requestChanges}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection={'column'} sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name='assigneeUser'
              rules={{ required: 'Please select an assignee' }}
              render={({ field, fieldState }) => (
                <TaskAssignUserPicker
                  projectId={projectId ?? task?.project?.id}
                  templateId={task.template.currentVersion.templateId}
                  stepId={task.getPreviousStep()?.id}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                />
              )}
            />

            <Controller
              name='comment'
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, ...field }, fieldState }) => {
                return (
                  <TextField
                    required
                    label={localizedStrings.comment}
                    placeholder={localizedStrings.comment}
                    data-test='comment-input'
                    rows={3}
                    sx={{ mt: 2 }}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      `${
                        field.value?.length || 0
                      }/${MAX_TASK_SUMMARY_CHARACTERS} ${
                        localizedStrings.characters
                      }`
                    }
                    onChange={(e) =>
                      e.target.value.length <= MAX_TASK_SUMMARY_CHARACTERS &&
                      onChange(e)
                    }
                    slotProps={{
                      formHelperText: {
                        error: !!fieldState.error,
                        sx: { pt: 0.5, pb: 2 },
                      },
                    }}
                    fullWidth
                    multiline
                    {...field}
                  />
                )
              }}
            />

            <Flex gap={2} mt={4}>
              <Button variant='outlined' onClick={hideModal} fullWidth>
                {localizedStrings.cancel}
              </Button>
              <Button
                variant='contained'
                type='submit'
                fullWidth
                data-test='save-task'
                disabled={requestChangesTaskMutation.isLoading}
              >
                {localizedStrings.save}
              </Button>
            </Flex>
          </form>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
